//#region Imports
import { useState, useEffect, useMemo } from "react";
import sharedDatas from "../../businessLogic/sharedDatas";
import { useNavigate } from "react-router-dom";

//oneFront SDK
import {
  useSnackbar,
  useMutation,
  useTranslation,
} from "@1f/react-sdk";

import VaporPage from "@vapor/react-custom/VaporPage";
import VaporTag from "@vapor/react-custom/VaporTag";

//Vapor Components
import Grid from "@vapor/react-material/Grid";
import Stack from "@vapor/react-material/Stack";
import IconButton from "@vapor/react-material/IconButton";
import Button from "@vapor/react-material/Button";
import Tooltip from "@vapor/react-material/Tooltip";

//Icons
import {
  Add as AddIcon,
  ArrowForward as DetailIcon,
  DeleteOutlineOutlined as DeleteIcon,  
} from "@mui/icons-material";

//Custom styles
import {
  chipGreen,
  chipYellow,
  chipOrange,
  chipRed,
  divGreen,
  divOrange,
  divRed
} from "../../businessLogic/styles";

import {
  DataGridPro,
  GridToolbar,
  itIT,
} from "@mui/x-data-grid-pro";

//Custom components
import { Loading } from "../../components/Loading";
import { NotAuth } from "../../components/NotAuth";
import { NoData } from "../../components/NoData";
import { ModalDelete } from "../../components/ModalDelete";

//Constants, Api and Data Models
import {  
  pathFascicoloHome,
  pathFascicoloNew,
  pathFascicoloAdd
} from "../../businessLogic/constants/path";

import { bsaFascicolo } from "../../businessLogic/constants/securityAreas";

import { setLocalStorageSelectedFascicolo } from "../../businessLogic/helpers/storageHelpers";

import { userPermissionModel } from "../../businessLogic/models";

import { mutation_add_audit } from "../../businessLogic/mutations/auditMutations";
import { mutation_add_lastActivity_byUserId, mutation_delete_lastActivity } from "../../businessLogic/mutations/lastActivityMutations";

import { FascicoloHelpers, isFascicoloCessato, mapFascicoloStatoFromBE } from "../../businessLogic/helpers/fascicoloHelpers";
import { LogApi } from "../../businessLogic/api/logApi";
import { FascicoloApi } from "../../businessLogic/api/fascicoloApi";
//#endregion

export const FascicoloHome = ({ basePath }) => {
  const tenantId = sharedDatas.getPropertyByName("tenantId");
  const aziendaId = sharedDatas.getPropertyByName("aziendaId");
  const loggedUserId = sharedDatas.getPropertyByName("userId");
  const aziendaName = sharedDatas.getPropertyByName("aziendaName");
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  //#region API/Helpers
  const fascicoloHelpers = FascicoloHelpers();
  const fascicoloApi = FascicoloApi(); 
  const logApi = LogApi()
  //#endregion

  //#region Mutation
  const apiAddAudit = useMutation(mutation_add_audit, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  const apiAddLastActivity = useMutation(mutation_add_lastActivity_byUserId, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  const apiDeleteLastActivity = useMutation(mutation_delete_lastActivity, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  //#endregion

  //#region UseStates
  const [backDropOpen, setBackDropOpen] = useState(true); //Loader utilizzato durante il caricamento
  const [isDataLoaded, setIsDataLoaded] = useState(false); //Indica lo stato di caricamento del result Api
  const [isTenantEnabled, setIsTenantEnabled] = useState(false);
  const [userPermissions, setUserPermissions] = useState(
    new userPermissionModel(
      0,
      aziendaId,
      bsaFascicolo,
      false,
      false,
      false,
      false
    )
  );
  const [dataItems, setDataItems] = useState([]); //Contiene i records
  const dataColumns = useMemo(
    () => [
      { field: "id", headerName: "Id", type: "number", flex: 0.2 },
      {
        field: "status",
        headerName: "Stato",
        type: "string",
        flex: 0.2,
        renderCell: (params) => renderStatusFascicolo(params.row),
        valueGetter: (params) => renderStatusFascicoloS(params.row),
      },
      {
        field: "name",
        headerName: "Intestazione",
        type: "string",
        flex: 1,
        editable: false,
        renderCell: (params) =>
        params.row.isDisabled ? (
          <Tooltip title={`Non è possibile aprire questo fascicolo perchè contiene una anagrafica che risulta essere stata identificata da ${params.row.identifiedBy} che non ha i necessari permessi per il soggetto obbligato corrente ${params.row.aziendaName}`}>
            <span style={{ width: "100%", display: "block" }}>{params.value}</span>
          </Tooltip>
        ) : (
          params.value
        )            
      },
      {
        field: "isPrivate",
        headerName: t("fascicolo.form.privato"),
        type: "string",
        flex: 0.3,
        editable: false,
        renderCell: (params) =>
        params.row.isDisabled ? (
          <Tooltip title={`Non è possibile aprire questo fascicolo perchè contiene una anagrafica che risulta essere stata identificata da ${params.row.identifiedBy} che non ha i necessari permessi per il soggetto obbligato corrente ${params.row.aziendaName}`}>
            <span style={{ width: "100%", display: "block" }}>{params.value}</span>
          </Tooltip>
        ) : (
          params.value
        )            
      },
      {
        field:"cliType",
        headerName:"Tipo Cliente",
        editable:false,
        flex:0.2,
        renderCell: (params) =>
        params.row.isDisabled ? (
          <Tooltip title={`Non è possibile aprire questo fascicolo perchè contiene una anagrafica che risulta essere stata identificata da ${params.row.identifiedBy} che non ha i necessari permessi per il soggetto obbligato corrente ${params.row.aziendaName}`}>
            <span style={{ width: "100%", display: "block" }}>{params.value}</span>
          </Tooltip>
        ) : (
          params.value
        )            
      },
      {
        field: "rischioAreaGeo",
        headerName: "Rischio area geografica cliente",
        type: "string",
        flex: 0.4,
        renderCell: (params) => renderLivelloRischio(params.row),
        valueGetter: (params) => renderLivelloRischioS(params.row),              
      },
      {
        field: "createdOn",
        headerName: t("fascicolo.form.creatoIl"),
        type: "date",
        flex: 0.2,
        valueFormatter: (params) => {
          if (params.value == null) {
            return "";
          }
          return new Date(params.value).toLocaleDateString();
        },
        renderCell: (params) =>
        params.row.isDisabled ? (
          <Tooltip title={`Non è possibile aprire questo  fascicolo perchè contiene una anagrafica che risulta essere stata identificata da ${params.row.identifiedBy} che non ha i necessari permessi per il soggetto obbligato corrente ${params.row.aziendaName}`}>
            <span style={{ width: "100%", display: "block" }}>{new Date(params.value).toLocaleDateString()}</span>
          </Tooltip>
        ) : (
          new Date(params.value).toLocaleDateString()
        )            
      },
      {
        field: "modifiedOn",
        headerName: t("fascicolo.form.modificatoIl"),
        type: "dateTime",
        flex: 0.2,
        valueFormatter: (params) => {
          if (params.value == null) {
            return "";
          }
          return params.value ? new Date(params.value).toLocaleDateString() : "";
        },
        renderCell: (params) => {
          if (params.value == null) {
            return params.row.isDisabled ? (
              <Tooltip title={`Non è possibile aprire questo  fascicolo perchè contiene una anagrafica che risulta essere stata identificata da ${params.row.identifiedBy} che non ha i necessari permessi per il soggetto obbligato corrente ${params.row.aziendaName}`}>
                <span style={{ width: "100%", display: "block" }}>-</span>
              </Tooltip>
            ) : (
              ""
            );
          }
          return params.row.isDisabled ? (
            <Tooltip title={`Non è possibile aprire questo  fascicolo perchè contiene una anagrafica che risulta essere stata identificata da ${params.row.identifiedBy} che non ha i necessari permessi per il soggetto obbligato corrente ${params.row.aziendaName}`}>
              <span style={{ width: "100%", display: "block" }}>{new Date(params.value).toLocaleDateString()}</span>
            </Tooltip>
          ) : (
            new Date(params.value).toLocaleDateString()
          );
        }
      },
      {
        field: "actions",
        headerName: "",
        flex: 0.2,
        filterable: false,
        renderCell: (params) =>
          renderDetailsButton(userPermissions, params.row),
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, userPermissions]
  ); //Contiene le columns
  const [dataRows, setDataRows] = useState([]); //Contiene le rows
  const [showDeleteModal, setShowDeleteModal] = useState(false); //Modal per la conferma di eliminazione di un record
  const [idToPerform, setIdToPerform] = useState(0); //Contiene l'id del record da eliminare
  //#endregion

  useEffect(() => {
    loadDatas();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataLoaded]);

  async function loadDatas() {
    try {
      if (
        sharedDatas.getPropertyByName("tenantId") === "" ||
        sharedDatas.getPropertyByName("tenantModel") === null ||
        sharedDatas.getPropertyByName("aziendaId") === 0 ||
        sharedDatas.getPropertyByName("aziendaModelList") === null ||
        sharedDatas.getPropertyByName("userId") === "" ||
        sharedDatas.getPropertyByName("userModel") === null
      ) {
        navigate("/");
      } else {
        setBackDropOpen(true);

        if (!isDataLoaded) {
          //Last Activity
          await apiDeleteLastActivity.fetch({
            tenantId: tenantId,
            aziendaId: aziendaId,
            userId: loggedUserId,
          });
          await apiAddLastActivity.fetch({
            tenantId: tenantId,
            aziendaId: aziendaId,
            path: pathFascicoloHome,
            entityId: "",
            entityName: "",
          });

          /**************************************/
          //Carico le informazioni relative al Tenant
          const tenantInfos = sharedDatas.getPropertyByName("tenantModel");
          const isEnabled =
            !tenantInfos.isReadOnly &&
            !tenantInfos.isDisabled &&
            !tenantInfos.isDeleted;
          setIsTenantEnabled(isEnabled);

          /**************************************/
          //Carico i permessi relativi all'utente
          const userPerms = sharedDatas
            .getPropertyByName("userPermissions")
            .filter((x) => x.area === bsaFascicolo)[0];
          setUserPermissions(userPerms);                   
          /**************************************/
          //Carico i fascicoli          
          const fascicoliResponse = await fascicoloApi.GetFascicoliAndRelatedEntities(tenantId,true,true,null) 
          const arrItems = [];
          const fascicoli = []
          for (var i = 0; i < fascicoliResponse.fascicoli.length; i++) {
            const fascicolo = fascicoliResponse.fascicoli[i];
            const { isCompleted, reasons } = mapFascicoloStatoFromBE(fascicolo);
            let rischioAreaGeo = fascicolo.anagrafica?.rischioGeografico || 4;                                                         
            let isCessato = isFascicoloCessato(fascicolo.prestazioni);
            let userName = ""            
            const itemModel = {
              id: fascicolo.id,
              status: isCompleted,
              statusReasons: reasons,
              isCessato : isCessato,
              name: fascicolo.name,
              isPrivate: fascicolo.isPrivate ? "Sì" : "No",
              rischioAreaGeo: rischioAreaGeo,
              createdOn: fascicolo.createdOn,
              modifiedOn: fascicolo.modifiedOn,
              cliType : fascicoloHelpers.GetClienteType(fascicolo.anagrafica || null),
              anagraficaFascicoli : fascicolo.anagrafica || [],              
              identifiedBy : userName,
              aziendaName : aziendaName,
              fascicoloData: fascicolo  // Salvo il fascicolo completo
            };
            arrItems.push(itemModel);
            fascicoli.push(fascicolo);
          }
          setDataItems(fascicoli);
          setDataRows(arrItems);
        }
      }
    } catch (e) {
      logApi.AddLog(e.message,e.stack,"loadDatas", "FascicoloHome")      
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
      setIsDataLoaded(true);
    }
  }    

  //#region Renderers
  const renderStatusFascicolo = (item) => {
    const status = item.status;
    const statusReasons = item.statusReasons;
    const isCessato = item.isCessato;
    let livelloS = "Incompleto";    

    if(item.status) {
        livelloS = "Completo";
    } else {
        livelloS = "Incompleto";
    }

    if(isCessato)
    {
      return (
        <div style={{ ...divRed }}>
          Cessato
        </div>
      );
    }

    if (status) {
      return (        
        <div style={{ ...(status === false ? divOrange : divGreen) }}>
          {livelloS}
        </div>
      );
    } else {
      return (
        <Tooltip
          title={
            <span style={{ whiteSpace: "pre-line" }}>{statusReasons}</span>
          }
          arrow
          placement='right'
        >
          <div style={{ ...(status === false ? divOrange : divGreen) }}>
            {livelloS}
          </div>
        </Tooltip>
      );
    }
  };
  const renderStatusFascicoloS = (item) => {
    let livelloS = "Incompleto";

    switch (item.status) {
      case true: {
        livelloS = "Completo";
        break;
      }
      case false: {
        livelloS = "Incompleto";
        break;
      }
      default: {
        break;
      }
    }

    if(item.isCessato){
      livelloS = "Cessato"
    }

    return livelloS;
  };
  const renderLivelloRischio = (item) => {
    let livelloS = t("valutazione.form.rischioInerenteLivello1");
    let livelloC = chipGreen;

    switch (item.rischioAreaGeo) {
      case 1: {
        livelloS = t("valutazione.form.rischioInerenteLivello1");
        livelloC = chipGreen;
        break;
      }
      case 2: {
        livelloS = t("valutazione.form.rischioInerenteLivello2");
        livelloC = chipYellow;
        break;
      }
      case 3: {
        livelloS = t("valutazione.form.rischioInerenteLivello3");
        livelloC = chipOrange;
        break;
      }
      case 4: {
        livelloS = t("valutazione.form.rischioInerenteLivello4");
        livelloC = chipRed;
        break;
      }
      default: {
        break;
      }
    }

    return <VaporTag variant='standard' label={livelloS} sx={livelloC} />;
  };
  const renderLivelloRischioS = (item) => {
    let livelloS = t("valutazione.form.rischioInerenteLivello1");

    switch (item.rischioAreaGeo) {
      case 1: {
        livelloS = t("valutazione.form.rischioInerenteLivello1");
        break;
      }
      case 2: {
        livelloS = t("valutazione.form.rischioInerenteLivello2");
        break;
      }
      case 3: {
        livelloS = t("valutazione.form.rischioInerenteLivello3");
        break;
      }
      case 4: {
        livelloS = t("valutazione.form.rischioInerenteLivello4");
        break;
      }
      default: {
        break;
      }
    }

    return livelloS;
  };
  const renderDetailsButton = (userPerms, item) => {
    return (
      <Stack direction='row' spacing={0}>
        {userPerms.canDelete ? (
          <IconButton
            color='error'
            onClick={(e) => handleGridBtnDeleteClick(e, item)}
          >
            <DeleteIcon />
          </IconButton>
        ) : null}
        {userPerms.canRead ? (
          <IconButton
            color='primary'
            onClick={(e) => { 
              if(!item.isDisabled){
                handleGridBtnDetailClick(e, item)
              }
            }}
          >
            <DetailIcon />
          </IconButton>
        ) : null}
      </Stack>
    );
  };
  //#endregion

  //#region Handlers
  const handleAddNew = () => {   
    navigate(pathFascicoloNew);
  };
  const handleGridBtnDetailClick = async (event, item) => {
    event.stopPropagation();
    // Uso direttamente il fascicolo salvato nell'item invece di filtrare dataItems
    setLocalStorageSelectedFascicolo(item.fascicoloData);
    navigate(pathFascicoloAdd);
  };
  const handleGridBtnDeleteClick = (event, item) => {
    event.stopPropagation();
    setIdToPerform(item.id);
    setShowDeleteModal(true);
  };
  //#endregion

  //#region Modal Delete
  const handleModalDeleteClose = () => {
    setIdToPerform(0);
    setShowDeleteModal(false);
  };
  const handleModalDeleteOkClick = async () => {
    try {
      //Eliminazione singola
      if (idToPerform > 0) {
        setBackDropOpen(true);
        let deleteResponse = await fascicoloApi.DeleteFascicolo(idToPerform);
        if (!deleteResponse) {
          enqueueSnackbar(t("error.generale"), { variant: "error" });
        } else {
          //Aggiungo la Audit per tracciare l'azione
          const itemById = JSON.stringify(
            dataItems.filter((item) => item.id === parseInt(idToPerform))[0]
          );
          apiAddAudit.fetch({
            tenantId: tenantId,
            aziendaId: aziendaId,
            area: bsaFascicolo,
            operation: "Delete",
            entityType: "fascicolo",
            entityId: idToPerform.toString(),
            oldValues: itemById,
            newValues: "",
          });

          //Resetto le variabili
          setIdToPerform(0);
          setShowDeleteModal(false);
          setIsDataLoaded(false);
          //loadDatas();
          enqueueSnackbar(t("message.success"), { variant: "success" });
        }
        setBackDropOpen(false);
      }
    } catch (e) {
      logApi.AddLog(e.message,e.stack,"handleModalDeleteOkClick", "FascicoloHome")      
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    }
  };
  //#endregion

  //#region Reutrning Graphics
  return (
    <VaporPage
      title={t("nav.fascicoli")}
      headerLeft={
        <Button
          variant='text'
          size='medium'
          endIcon={<AddIcon />}
          color='primary'
          disabled
          tabIndex={-1}
          aria-hidden='true'
          sx={{ mr: 2, visibility: "hidden", pointerEvents: "none" }}
        >
          {t("actions.nuovo")}
        </Button>
      }
      headerRight={
        <Button
          variant='text'
          size='medium'
          endIcon={<AddIcon />}
          color='primary'
          onClick={handleAddNew}
          disabled={!isTenantEnabled || !userPermissions.canCreate}
          sx={{ mr: 2 }}
        >
          {t("actions.nuovo")}
        </Button>
      }
    >
      <Loading open={backDropOpen} />

      {isDataLoaded && !userPermissions.canRead ? <NotAuth /> : null}

      {showDeleteModal ? (
        <ModalDelete
          show={showDeleteModal}
          onClose={handleModalDeleteClose}
          onOk={handleModalDeleteOkClick}
          deleteMessage={t("modal.deleteDesc")}
        ></ModalDelete>
      ) : null}

      {isDataLoaded && userPermissions.canRead ? (
        <VaporPage.Section>
          <Grid container>
            <Grid item xs={12} sx={{ width: "100%" }}>
              {dataRows.length > 0 ? (
                <DataGridPro
                  rows={dataRows}
                  columns={dataColumns}
                  slots={{ toolbar: GridToolbar }}
                  getRowId={(row) => row.id}
                  isRowSelectable={(params) => !params.row.isDisabled}                                       
                  getRowClassName={(params) => params.row.isDisabled ? 'row-disabled' : ''}                 
                  autoHeight={true}
                  pageSizeOptions={[25, 50, 100]}
                  localeText={
                    itIT.components.MuiDataGrid.defaultProps.localeText
                  }
                  pagination
                  disableRowSelectionOnClick
                  onRowClick={(item, e) => {
                    if (!item.row.isDisabled) {
                      handleGridBtnDetailClick(e, item.row);
                    }
                  }}
                  initialState={{
                    columns: {
                      columnVisibilityModel: {
                        id: false,
                        createdOn: false,
                      },
                    },
                    pagination: {
                      paginationModel: {
                        pageSize: 25,
                      },
                    },
                  }}
                  slotProps={{
                    toolbar: { printOptions: { disableToolbarButton: true } },
                  }}
                />
              ) : (
                <NoData />
              )}
            </Grid>
          </Grid>
        </VaporPage.Section>
      ) : null}
    </VaporPage>
  );
  //#endregion
};
